import { useState, useMemo, useEffect, useRef } from 'react';
import type { PlaylistItem } from '@/shared/types';
import type { YoutubeSearchResult } from '@/shared/models/playlist.model';
import { filtrarPlaylist, separarPorIncorporacao, filtrarDuracao } from '../../domain/usecases/buscarMusicas';
import { calcularEstadoItem } from '../../domain/usecases/validarPedido';
import { getClientePrimeiroNome } from '../../domain/usecases/clienteDisplay';
import type { FilaJukebox } from '../../domain/entities/FilaJukebox';
import { SongCard } from './SongCard';
import { MobileSheet } from '@/shared/components/MobileSheet';

type StatusSugestao = 'idle' | 'buscando' | 'enviando' | 'aguardando-aprovacao' | 'erro';

// A busca filtra conforme digita — o cliente chega no que quer digitando,
// não rolando — então não há scroll infinito aqui. Isso limita o pior caso
// (playlist inteira, termo vazio) a um número de montagens de `SongCard`
// seguro num Android barato, evitando centenas de useEffect/setTimeout no
// mesmo frame em que o teclado abre.
const LIMITE_VISIVEL = 40;

export interface JukeboxBuscaViewProps {
  items:            PlaylistItem[];
  filaAtiva:        FilaJukebox[];
  cooldowns:        ReadonlyMap<string, number>;
  tocandoVideoId:   string | null;
  meuSessionId:     string;
  agora:            number;
  pedindoId:        string | null;
  onPedir:          (item: PlaylistItem) => void;
  onFechar:         () => void;
  tema:             { songBtnBg: string; songBtnIcon: string; searchBtn: string };
  resultadosBusca:  YoutubeSearchResult[];
  statusSugestao:   StatusSugestao;
  erroSugestao:     string;
  onBuscarYoutube:  (termo: string) => void;
  onEnviarSugestao: (video: YoutubeSearchResult) => void;
  clienteNome?:     string | null;
}

function formatDuracao(s: number | null | undefined): string | null {
  if (!s || s <= 0) return null;
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * Busca full-screen — uma superfície só para "achar uma música", seja no
 * repertório da casa ou no YouTube.
 *
 * O campo fica ancorado no TOPO de propósito: o teclado virtual sobe pela
 * parte de baixo, então um campo no topo nunca é coberto, em nenhum
 * sistema. Não há nada `fixed` na parte inferior desta view — sem bottom
 * nav, sem FAB, sem header sticky — pelo mesmo motivo.
 */
export function JukeboxBuscaView({
  items, filaAtiva, cooldowns, tocandoVideoId, meuSessionId, agora, pedindoId,
  onPedir, onFechar, tema, resultadosBusca, statusSugestao, erroSugestao,
  onBuscarYoutube, onEnviarSugestao, clienteNome,
}: JukeboxBuscaViewProps) {
  const [termo, setTermo] = useState('');
  // `alvo` = vídeo aguardando confirmação no sheet; `enviada` = último vídeo
  // sugerido, que fica na tela mesmo depois que o hook volta a `idle` (5s).
  const [alvo, setAlvo] = useState<YoutubeSearchResult | null>(null);
  const [enviada, setEnviada] = useState<YoutubeSearchResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const primeiroNome = getClientePrimeiroNome(clienteNome);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const pedirConfirmacao = (video: YoutubeSearchResult) => {
    inputRef.current?.blur();
    setAlvo(video);
  };

  // Só vira "enviada" quando o hook confirma o envio — duplicada/erro não.
  const enviandoRef = useRef<YoutubeSearchResult | null>(null);
  useEffect(() => {
    if (statusSugestao === 'aguardando-aprovacao' && enviandoRef.current) {
      setEnviada(enviandoRef.current);
      enviandoRef.current = null;
    } else if (statusSugestao === 'erro') {
      enviandoRef.current = null;
    }
  }, [statusSugestao]);

  const confirmarSugestao = () => {
    if (!alvo) return;
    enviandoRef.current = alvo;
    setAlvo(null);
    onEnviarSugestao(alvo);
  };

  const buscarYoutube = (t: string) => {
    setEnviada(null);
    onBuscarYoutube(t);
  };

  const filtrados = useMemo(() => filtrarPlaylist(items, termo), [items, termo]);

  // Vídeos muito longos quase nunca são a música que o cliente quer; e os que o
  // dono proíbe tocar fora do YouTube falhariam na TV (101/150) depois da aprovação.
  // Formato/gênero não filtra: o gerente já aprova cada sugestão.
  const { tocaveis: resultadosYt, bloqueados: ocultosPorEmbed } = useMemo(
    () => separarPorIncorporacao(
      filtrarDuracao(resultadosBusca),
    ),
    [resultadosBusca],
  );

  // Resultado do YouTube que já é do repertório vira um SongCard pedível, em vez
  // de uma sugestão que o hook recusaria só no fim ("já está na playlist").
  const itemPorVideoId = useMemo(() => new Map(items.map(i => [i.video_id, i])), [items]);

  const termoLimpo = termo.trim();

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col bg-jukebox-bg"
      style={{ height: 'var(--vv-height, 100dvh)' }}
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-white/[0.05] px-3 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={onFechar}
          aria-label="Voltar"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <input
          ref={inputRef}
          type="search"
          value={termo}
          onChange={e => setTermo(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && termoLimpo && statusSugestao !== 'buscando') buscarYoutube(termoLimpo); }}
          placeholder="Buscar música ou artista..."
          aria-label="Buscar música"
          className="
            min-w-0 flex-1 rounded-2xl border border-white/10 bg-black/30
            px-4 py-3 text-base text-white placeholder:text-white/50
            outline-none transition-colors focus:border-orange-500 focus:bg-black/40
          "
        />

        {termo && (
          <button
            type="button"
            onClick={() => setTermo('')}
            aria-label="Limpar busca"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white/50 hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 transition-colors"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-8 pt-4">
        <h2 className="mb-3 text-[11px] font-medium text-white/50">
          No repertório {termoLimpo && `· ${filtrados.length}`}
        </h2>

        {filtrados.length > 0 ? (
          <div className="space-y-2">
            {filtrados.slice(0, LIMITE_VISIVEL).map(item => {
              const { estado, tempoRestante } = calcularEstadoItem(
                item.video_id, filaAtiva, cooldowns, tocandoVideoId, meuSessionId, agora,
              );
              return (
                <SongCard
                  key={item.id}
                  item={item}
                  estado={estado}
                  tempoRestante={tempoRestante}
                  onPedir={onPedir}
                  pedindo={pedindoId === item.id}
                  songBtnBg={tema.songBtnBg}
                  songBtnIcon={tema.songBtnIcon}
                />
              );
            })}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-white/50">
            Nada no repertório {termoLimpo && `para “${termoLimpo}”`}
          </p>
        )}

        {termoLimpo && (
          <div className="mt-8 border-t border-white/[0.05] pt-6">
            <div className="mb-1 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white/80">Não achou? Sugira ao gerente</h2>
              {primeiroNome && (
                <span className="text-[11px] text-white/50">Sugerindo como {primeiroNome}</span>
              )}
            </div>
            <p className="mb-3 text-xs text-white/55">
              A sugestão não toca na hora: o gerente precisa aprovar antes de entrar na playlist.
            </p>

            <button
              type="button"
              onClick={() => buscarYoutube(termoLimpo)}
              disabled={statusSugestao === 'buscando'}
              className={`
                flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r px-5 py-3
                text-sm font-semibold text-white transition-transform active:scale-95
                disabled:cursor-not-allowed disabled:opacity-40
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${tema.searchBtn}
              `}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              Buscar para sugerir
            </button>

            {statusSugestao === 'buscando' && (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
              </div>
            )}

            {enviada && (
              <div className="mt-4 flex gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.08] p-3">
                <img
                  src={enviada.thumbnail}
                  alt=""
                  aria-hidden
                  className="h-16 w-16 shrink-0 rounded-xl object-cover"
                />
                <div className="flex min-w-0 flex-1 flex-col justify-center">
                  <h4 className="line-clamp-2 text-sm font-semibold leading-snug text-white/90">{enviada.titulo}</h4>
                  <p className="mt-1 text-xs font-semibold text-amber-300">Aguardando aprovação do gerente</p>
                  <p className="text-xs text-white/55">Ainda não está na fila.</p>
                </div>
              </div>
            )}

            {ocultosPorEmbed > 0 && (
              <p className="mt-4 text-center text-xs text-white/55">
                {ocultosPorEmbed === 1
                  ? '1 resultado foi ocultado'
                  : `${ocultosPorEmbed} resultados foram ocultados`}
                {' '}porque o YouTube não permite tocá-los fora dele.
              </p>
            )}

            {erroSugestao && (
              <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/[0.08] p-4 text-center text-sm text-red-400/80">
                {erroSugestao}
              </div>
            )}

            <div className="mt-4 space-y-2">
              {resultadosYt.slice(0, 10).map(video => {
                const naPlaylist = itemPorVideoId.get(video.videoId);
                if (naPlaylist) {
                  const { estado, tempoRestante } = calcularEstadoItem(
                    naPlaylist.video_id, filaAtiva, cooldowns, tocandoVideoId, meuSessionId, agora,
                  );
                  return (
                    <SongCard
                      key={video.videoId}
                      item={naPlaylist}
                      estado={estado}
                      tempoRestante={tempoRestante}
                      onPedir={onPedir}
                      pedindo={pedindoId === naPlaylist.id}
                      songBtnBg={tema.songBtnBg}
                      songBtnIcon={tema.songBtnIcon}
                      selo="Já está na playlist"
                    />
                  );
                }
                const durStr = formatDuracao(video.duracao_segundos);
                return (
                  <button
                    key={video.videoId}
                    onClick={() => pedirConfirmacao(video)}
                    disabled={statusSugestao !== 'idle' && statusSugestao !== 'erro'}
                    className="group flex w-full gap-3 rounded-2xl border border-white/[0.05] bg-white/[0.03] p-3 text-left transition-all hover:border-orange-500/30 hover:bg-white/[0.05] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
                  >
                    <div className="relative shrink-0">
                      <img
                        src={video.thumbnail}
                        alt=""
                        aria-hidden
                        loading="lazy"
                        className="h-16 w-16 rounded-xl object-cover"
                      />
                      {durStr && (
                        <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1 py-px font-mono text-[10px] leading-none text-white">
                          {durStr}
                        </span>
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-center">
                      <h4 className="line-clamp-2 text-sm font-semibold leading-snug text-white/90">{video.titulo}</h4>
                      <p className="mt-0.5 text-xs text-white/55">{video.canal}</p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                          Precisa de aprovação
                        </span>
                        <span className="text-xs font-semibold text-orange-400/80">Sugerir ao gerente →</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <MobileSheet aberto={!!alvo} titulo="Confirmar sugestão" onClose={() => setAlvo(null)}>
        {alvo && (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-2">
            <div className="flex gap-3">
              <img
                src={alvo.thumbnail}
                alt=""
                aria-hidden
                className="h-16 w-16 shrink-0 rounded-xl object-cover"
              />
              <div className="flex min-w-0 flex-1 flex-col justify-center">
                <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-white/90">{alvo.titulo}</h3>
                <p className="mt-0.5 text-xs text-white/55">{alvo.canal}</p>
              </div>
            </div>

            <p className="mt-4 text-sm text-white/70">
              Isso não toca na hora. O gerente precisa aprovar antes.
            </p>

            <button
              type="button"
              onClick={confirmarSugestao}
              className={`mt-5 w-full rounded-2xl bg-gradient-to-r px-5 py-3 text-sm font-semibold text-white transition-transform active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${tema.searchBtn}`}
            >
              Enviar sugestão ao gerente
            </button>
            <button
              type="button"
              onClick={() => setAlvo(null)}
              className="mt-2 w-full rounded-2xl px-5 py-3 text-sm font-medium text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              Cancelar
            </button>
          </div>
        )}
      </MobileSheet>
    </div>
  );
}
